import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getDiscoverChatContext } from '../shared/discoverChatContext';
import './ChatFilterWizard.css';

// MOCKED AI parser — replace with real Langflow call in WP02.
// Picks plausible filter rows from a few keywords so the UX demo feels real.
function mockGenerateFilter(prompt) {
  const p = prompt.toLowerCase();
  const rows = [];

  // Patent-id-shaped tokens, e.g. US9567581, US9567581-9121, EP1234567-A1, WO2020/12345-1
  const idTokenRe = /\b([A-Z]{2}\d{4,})(?:([-/])([A-Z0-9]+))?\b/g;
  const idMatches = [...prompt.matchAll(idTokenRe)];

  // Explicit field intents
  const wantsSubjectId = /\bsubject\s*(sequence)?\s*id\b/.test(p);
  const wantsPatentNum = /\b(patent\s*(number|no\.?|#)|patent_?n|\bpn\b)\b/.test(p);
  const wantsTitle     = /\btitle\b/.test(p);
  const wantsPubDate   = /\b(publication|publish(ed)?|pub)\s*date\b/.test(p) || /\bpublished\b/.test(p);

  // Subject Sequence ID vs Patent Number routing
  if (idMatches.length > 0) {
    const m = idMatches[0];
    const fullToken = m[0];
    const hasDashSuffix = !!m[2];
    if (wantsSubjectId) {
      rows.push({ field: 'SUBJECT_ID', fieldLabel: 'Subject ID', dataType: 'string', operator: 'EQUALS', value: fullToken });
    } else if (wantsPatentNum) {
      rows.push({ field: 'SUBJECT_PN', fieldLabel: 'Patent Number', dataType: 'string', operator: 'EQUALS', value: m[1] });
    } else if (hasDashSuffix) {
      rows.push({ field: 'SUBJECT_ID', fieldLabel: 'Subject ID', dataType: 'string', operator: 'EQUALS', value: fullToken });
    } else {
      rows.push({ field: 'SUBJECT_PN', fieldLabel: 'Patent Number', dataType: 'string', operator: 'EQUALS', value: m[1] });
    }
  } else if (wantsSubjectId || wantsPatentNum) {
    // Field mentioned but no token detected — try a trailing-value fallback
    const tail = prompt.match(/(?:id|equal[s]?|=|is|has)\s*[:=]?\s*([A-Za-z0-9_\-/]+)\s*$/i);
    if (tail) {
      const field = wantsSubjectId ? 'SUBJECT_ID' : 'SUBJECT_PN';
      const label = wantsSubjectId ? 'Subject ID' : 'Patent Number';
      rows.push({ field, fieldLabel: label, dataType: 'string', operator: 'EQUALS', value: tail[1] });
    }
  }

  // Title: "title contains X", "title is X", "title: X"
  if (wantsTitle) {
    const tEq  = prompt.match(/title\s*(?:is|equals?)\s*[:=]?\s*"?([^"]+?)"?\s*$/i);
    const tCt  = prompt.match(/title\s*(?:contains?|has|with|:)\s*[:=]?\s*"?([^"]+?)"?\s*$/i);
    if (tEq) {
      rows.push({ field: 'SUBJECT_PT', fieldLabel: 'Title', dataType: 'string', operator: 'EQUALS', value: tEq[1].trim() });
    } else if (tCt) {
      rows.push({ field: 'SUBJECT_PT', fieldLabel: 'Title', dataType: 'string', operator: 'CONTAINS', value: tCt[1].trim() });
    }
  }

  // Authority
  const authorityMap = { uspto: 'USPTO', epo: 'EPO', wipo: 'WIPO', jpo: 'JPO' };
  for (const [k, v] of Object.entries(authorityMap)) {
    if (p.includes(k)) {
      rows.push({ field: 'SUBJECT_PA', fieldLabel: 'Patent Authority', dataType: 'string_frame', operator: 'EQUALS', value: v });
      break;
    }
  }

  // Publication date — full ISO, US-style, or year-only. Direction inferred from words.
  const dirOp = p.includes('before') ? 'LESS' : (p.includes('after') || p.includes('since') || p.includes('from')) ? 'MORE' : (p.includes(' on ') ? 'EQUALS' : 'MORE');
  const isoMatch = prompt.match(/\b(\d{4})-(\d{1,2})-(\d{1,2})\b/);
  const usMatch  = prompt.match(/\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/);
  const yearMatch = !isoMatch && !usMatch ? prompt.match(/\b(19|20)\d{2}\b/) : null;
  if (isoMatch) {
    const v = `${isoMatch[1]}-${isoMatch[2].padStart(2, '0')}-${isoMatch[3].padStart(2, '0')}`;
    rows.push({ field: 'SUBJECT_D1', fieldLabel: 'Publication Date', dataType: 'date', operator: dirOp, value: v });
  } else if (usMatch) {
    const v = `${usMatch[3]}-${usMatch[1].padStart(2, '0')}-${usMatch[2].padStart(2, '0')}`;
    rows.push({ field: 'SUBJECT_D1', fieldLabel: 'Publication Date', dataType: 'date', operator: dirOp, value: v });
  } else if (yearMatch && (wantsPubDate || /\b(year|in)\b/.test(p) || rows.length === 0)) {
    rows.push({ field: 'SUBJECT_D1', fieldLabel: 'Publication Date', dataType: 'date', operator: dirOp, value: `${yearMatch[0]}-01-01` });
  }

  // % Identity
  const identityMatch = p.match(/identity\s*(?:>|>=|over|above|greater than)?\s*(\d{1,3})/);
  if (identityMatch) {
    rows.push({ field: 'RESULT_RI', fieldLabel: '% Identity', dataType: 'percentage', operator: 'MORE', value: identityMatch[1] });
  }

  // Patent status
  if (p.includes('granted')) {
    rows.push({ field: 'SUBJECT_PG', fieldLabel: 'Patent Status', dataType: 'string_frame', operator: 'EQUALS', value: 'Granted' });
  } else if (p.includes('pending')) {
    rows.push({ field: 'SUBJECT_PG', fieldLabel: 'Patent Status', dataType: 'string_frame', operator: 'EQUALS', value: 'Pending' });
  }

  // Last-resort fallback: title CONTAINS first few words
  if (rows.length === 0) {
    rows.push({ field: 'SUBJECT_PT', fieldLabel: 'Title', dataType: 'string', operator: 'CONTAINS', value: prompt.split(' ').slice(0, 3).join(' ') });
  }
  return rows;
}

// Render a chat-friendly summary of the generated rows so the user sees what
// will be saved without exposing editable form controls.
function summarizeRows(rows) {
  return rows.map(r => `• ${r.fieldLabel || r.field} ${r.operator.replace(/_/g, ' ').toLowerCase()} "${r.value}"`).join('\n');
}

const ChatFilterWizard = ({ onClose }) => {
  const [chat, setChat] = useState([
    { role: 'bot', text: "Describe the filter you'd like. Examples:\n• Subject Sequence ID = US9567581-9121\n• Patent number US9567581\n• Title contains antibody\n• Published after 2023-01-01\n• USPTO granted patents with identity above 80%" },
  ]);
  const [step, setStep] = useState('prompt');
  const [prompt, setPrompt] = useState('');
  const [rows, setRows] = useState([]);
  const [filterName, setFilterName] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chat, step]);

  const addBot = useCallback((text) => setChat(prev => [...prev, { role: 'bot', text }]), []);
  const addUser = useCallback((text) => setChat(prev => [...prev, { role: 'user', text }]), []);

  const handlePromptSubmit = () => {
    if (!prompt.trim()) return;
    addUser(prompt);
    setStep('generating');
    setTimeout(() => {
      const generated = mockGenerateFilter(prompt);
      setRows(generated);
      addBot(`Built ${generated.length} condition${generated.length > 1 ? 's' : ''}:\n${summarizeRows(generated)}\n\nGive this filter a name (it will appear under Advanced Filters):`);
      setStep('name');
    }, 1200);
  };

  const handleNameSubmit = () => {
    const name = filterName.trim();
    if (!name) return;
    addUser(name);
    const ctx = getDiscoverChatContext();
    if (!ctx || typeof ctx.addAiCustomFilter !== 'function') {
      addBot('Could not reach the Discover filter system. Make sure you are on the Discover page.');
      setStep('done');
      return;
    }
    const result = ctx.addAiCustomFilter({ name, rows });
    if (result && result.ok) {
      const verb = result.overwritten ? 'updated' : 'added';
      addBot(`Done — "${name}" was ${verb} under Advanced Filters, applied, and the results are refreshing.`);
    } else {
      addBot(`Could not add the filter${result && result.error ? `: ${result.error}` : '.'}`);
    }
    setStep('done');
  };

  return (
    <div className="cfw-container">
      <div className="cfw-header">
        <span className="cfw-title">🪄 AI Filter Builder</span>
        <button className="cfw-close" onClick={onClose} title="Close">×</button>
      </div>

      <div className="cfw-chat">
        {chat.map((m, i) => (
          <div key={i} className={`cfw-msg cfw-msg-${m.role}`} style={{ whiteSpace: 'pre-wrap' }}>{m.text}</div>
        ))}

        {step === 'generating' && (
          <div className="cfw-msg cfw-msg-bot cfw-loading">
            <span className="cfw-dots"><span /><span /><span /></span> Analyzing your filter request...
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <div className="cfw-actions">
        {step === 'prompt' && (
          <>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder='e.g. Subject Sequence ID = US9567581-9121'
              rows={2}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePromptSubmit(); } }}
            />
            <button className="cfw-btn cfw-btn-primary" onClick={handlePromptSubmit} disabled={!prompt.trim()}>Generate</button>
          </>
        )}
        {step === 'name' && (
          <>
            <input
              type="text"
              value={filterName}
              onChange={e => setFilterName(e.target.value)}
              placeholder="Filter name (e.g. My USPTO filter)"
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleNameSubmit(); } }}
              autoFocus
            />
            <button className="cfw-btn cfw-btn-primary" onClick={handleNameSubmit} disabled={!filterName.trim()}>
              Add to Advanced Filters
            </button>
          </>
        )}
        {step === 'done' && (
          <button className="cfw-btn cfw-btn-primary" onClick={onClose}>Close</button>
        )}
      </div>
    </div>
  );
};

export default ChatFilterWizard;
